/**
 * M27 task-07 — Web Research providers (ADM-15/16/17; HUB-084). One card per
 * web capability (search / extract / nav) rendered from the shared
 * `WEB_CAPABILITIES` single-home — no hand-copied list. Each card follows the
 * §2.1 "params + Enabled checkbox" shape: a vendor id, a WRITE-ONLY API key
 * (response carries only `hasApiKey`), an Enabled toggle, and — for `nav`
 * (Browserbase) — an optional project id.
 *
 * The card is pure/controlled so the no-jsdom seam test can render it via
 * `renderToStaticMarkup`.
 */
import { useEffect, useState } from 'react';
import {
  WEB_CAPABILITIES,
  WEB_PROVIDER_VENDORS,
  type WebCapability,
  type WebProvider,
} from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { SecretField } from '@/components/SecretField';
import { hub } from '@/lib/hub-client';

/** The 3 capabilities straight from the shared single-home (no hand copy). */
export const webCapabilities: WebCapability[] = Object.values(WEB_CAPABILITIES);

const CAPABILITY_COPY: Record<WebCapability, { title: string; hint: string }> = {
  search: { title: 'Search', hint: 'Web search results for a query.' },
  extract: { title: 'Extract', hint: 'Clean content extraction from a URL.' },
  nav: { title: 'Navigate', hint: 'Headless multi-step browsing.' },
};

/**
 * The selectable vendors per capability, straight from the shared
 * `WEB_PROVIDER_VENDORS` single-home — the hub's provider registry is typed
 * against the same map. `provider` is matched EXACTLY server-side and an
 * unknown id fails closed (`503 web_capability_disabled`), so this is a
 * dropdown, never free text.
 */
export function vendorsFor(capability: WebCapability): readonly string[] {
  return WEB_PROVIDER_VENDORS[capability];
}

export interface CapabilityCardProps {
  capability: WebCapability;
  configured: boolean;
  hasApiKey: boolean;
  provider: string;
  apiKey: string;
  enabled: boolean;
  projectId: string;
  busy: boolean;
  onProvider: (v: string) => void;
  onApiKey: (v: string) => void;
  onEnabled: (v: boolean) => void;
  onProjectId: (v: string) => void;
  onSave: () => void;
  onDelete: () => void;
}

/** One web-capability provider card (exported seam). `nav` shows a project id. */
export function CapabilityCard(props: CapabilityCardProps) {
  const {
    capability,
    configured,
    hasApiKey,
    provider,
    apiKey,
    enabled,
    projectId,
    busy,
    onProvider,
    onApiKey,
    onEnabled,
    onProjectId,
    onSave,
    onDelete,
  } = props;
  const copy = CAPABILITY_COPY[capability];
  const vendors = vendorsFor(capability);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{copy.title}</CardTitle>
          <span className={configured ? 'text-xs font-medium text-emerald-600' : 'text-xs text-muted-foreground'}>
            {configured ? (enabled ? 'Enabled' : 'Configured (disabled)') : 'Not configured'}
          </span>
        </div>
        <CardDescription>{copy.hint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={`${capability}-provider`}>Provider</Label>
          <Select
            id={`${capability}-provider`}
            aria-label={`Provider for ${capability}`}
            className="w-full"
            value={provider}
            onChange={(e) => onProvider(e.target.value)}
          >
            <option value="" disabled>
              Select a provider
            </option>
            {vendors.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
            {/* A row stored with an id the hub has no integration for (only
                reachable by calling the API directly) is shown as-is rather
                than silently displayed as a supported vendor. */}
            {provider !== '' && !vendors.includes(provider) && (
              <option value={provider}>{provider} (not supported)</option>
            )}
          </Select>
        </div>
        <SecretField
          id={`${capability}-apiKey`}
          label="API key"
          hasSecret={hasApiKey}
          value={apiKey}
          onChange={onApiKey}
        />
        {capability === 'nav' && (
          <div className="space-y-2">
            <Label htmlFor="nav-projectId">Browserbase project id</Label>
            <Input
              id="nav-projectId"
              placeholder="Optional"
              value={projectId}
              onChange={(e) => onProjectId(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
          Enabled
        </label>
        <div className="flex items-center justify-between">
          <Button type="button" onClick={onSave} disabled={busy || provider === ''}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          {configured && (
            <Button type="button" variant="outline" onClick={onDelete} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// --- container ---

export interface CapabilityFormState {
  provider: string;
  apiKey: string;
  enabled: boolean;
  projectId: string;
}
const EMPTY_FORM: CapabilityFormState = { provider: '', apiKey: '', enabled: false, projectId: '' };

/**
 * PUT body for a write-only key (exported seam). The key field is blank on every
 * load, so it is sent ONLY when the operator typed a replacement — omitting it
 * keeps the stored one, which is what makes an Enabled-only save possible. The
 * Browserbase project id rides along for `nav` only.
 */
export function putBody(
  capability: WebCapability,
  form: CapabilityFormState,
): { provider: string; apiKey?: string; enabled: boolean; config?: { projectId: string } } {
  return {
    provider: form.provider.trim(),
    enabled: form.enabled,
    ...(form.apiKey ? { apiKey: form.apiKey } : {}),
    ...(capability === 'nav' && form.projectId.trim() ? { config: { projectId: form.projectId.trim() } } : {}),
  };
}

export function WebResearch() {
  const [configs, setConfigs] = useState<Record<string, WebProvider | null>>({});
  const [forms, setForms] = useState<Record<string, CapabilityFormState>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    hub
      .webProvidersList()
      .then((rows: WebProvider[]) => {
        const byCapability = Object.fromEntries(rows.map((r) => [r.capability, r]));
        const nextConfigs: Record<string, WebProvider | null> = {};
        const nextForms: Record<string, CapabilityFormState> = {};
        for (const capability of webCapabilities) {
          const row = byCapability[capability] ?? null;
          nextConfigs[capability] = row;
          nextForms[capability] = row
            ? { provider: row.provider, apiKey: '', enabled: row.enabled, projectId: row.config?.projectId ?? '' }
            : { ...EMPTY_FORM };
        }
        setConfigs(nextConfigs);
        setForms(nextForms);
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  const patchForm = (capability: string, patch: Partial<CapabilityFormState>) =>
    setForms((prev) => ({ ...prev, [capability]: { ...(prev[capability] ?? EMPTY_FORM), ...patch } }));

  const save = async (capability: WebCapability) => {
    const form = forms[capability] ?? EMPTY_FORM;
    setBusyKey(capability);
    setError(null);
    try {
      const row = await hub.webProviderPut({ params: { capability }, body: putBody(capability, form) });
      setConfigs((prev) => ({ ...prev, [capability]: row }));
      patchForm(capability, { apiKey: '' });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (capability: WebCapability) => {
    setBusyKey(capability);
    setError(null);
    try {
      await hub.webProviderDelete({ params: { capability } });
      setConfigs((prev) => ({ ...prev, [capability]: null }));
      setForms((prev) => ({ ...prev, [capability]: { ...EMPTY_FORM } }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Web Research</h1>
        <p className="text-sm text-muted-foreground">
          The search, extract, and browse providers the copilot&apos;s web tools use. Keys are write-only.
        </p>
      </div>
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {webCapabilities.map((capability) => {
          const form = forms[capability] ?? EMPTY_FORM;
          const config = configs[capability] ?? null;
          return (
            <CapabilityCard
              key={capability}
              capability={capability}
              configured={config !== null}
              hasApiKey={config?.hasApiKey ?? false}
              provider={form.provider}
              apiKey={form.apiKey}
              enabled={form.enabled}
              projectId={form.projectId}
              busy={busyKey === capability}
              onProvider={(v) => patchForm(capability, { provider: v })}
              onApiKey={(v) => patchForm(capability, { apiKey: v })}
              onEnabled={(v) => patchForm(capability, { enabled: v })}
              onProjectId={(v) => patchForm(capability, { projectId: v })}
              onSave={() => void save(capability)}
              onDelete={() => void remove(capability)}
            />
          );
        })}
      </div>
    </div>
  );
}
